// utilies/moveTool/MoveToolController.js
import * as Cesium from "cesium";
import { createUniqueId, matchKeyInText } from "../Utility";
import { disableCameraControls, restoreCameraControls } from "../MapUtility";

// Destination keys
const TARGET_KEYS = {
  virtualline: "virtualline",
  curbstone: "curbstone",
  roadedge: "roadedge",
  lanemarker: "lanemarker",
};

// Lanelet relationship keys
const LANELET_KEY = "lanelet";
const LANELET_REL = {
  RIGHT_ID: "right_line_id",
  LEFT_ID: "left_line_id",
  RIGHT_TYPE: "right_line_type",
  LEFT_TYPE: "left_line_type",
};

const LINE_TYPE_BY_TARGET_KEY = {
  lanemarker: "marker",
  virtualline: "virtual",
};

function clampRect(a, b) {
  const left = Math.min(a.x, b.x);
  const right = Math.max(a.x, b.x);
  const top = Math.min(a.y, b.y);
  const bottom = Math.max(a.y, b.y);
  return { left, right, top, bottom, width: right - left, height: bottom - top };
}

function isEntity(obj) {
  if (!obj) return false;
  return (
    typeof obj === "object" &&
    "id" in obj &&
    (obj.position ||
      obj.point ||
      obj.polyline ||
      obj.polygon ||
      obj.billboard ||
      obj.label ||
      obj.rectangle ||
      obj.ellipse ||
      obj.corridor ||
      obj.wall ||
      obj.model ||
      obj.path ||
      obj.box ||
      obj.cylinder ||
      obj.plane)
  );
}

function getEntityFromPick(pick) {
  const id = pick?.id;
  if (isEntity(id)) return id;
  if (id && typeof id === "object" && isEntity(id.id)) return id.id;
  return null;
}

function getEntityLayerName(entity) {
  const now = Cesium.JulianDate.now();
  let props = {};
  try {
    props = entity?.properties?.getValue?.(now) || {};
  } catch {}

  const direct = entity?.layerName || entity?.layer_name || null;
  if (direct) return String(direct);

  const candidates = [props.layerName, props.layer_name, props.LayerName, props.LAYERNAME, props.layer, props.Layer, props.LAYER];

  for (const c of candidates) {
    if (typeof c === "string" && c.trim()) return c;
  }
  return "";
}

function getEntityPropsPlain(entity) {
  const now = Cesium.JulianDate.now();
  try {
    return entity?.properties?.getValue?.(now) || {};
  } catch {
    return {};
  }
}

function ensurePropertyBag(entity) {
  if (!entity) return null;
  const current = entity.properties;
  if (current && typeof current.addProperty === "function") return current;

  const snapshot = getEntityPropsPlain(entity);
  const bag = new Cesium.PropertyBag({});
  for (const k of Object.keys(snapshot)) {
    bag.addProperty(k, new Cesium.ConstantProperty(snapshot[k]));
  }
  entity.properties = bag;
  return bag;
}

function setBagConst(bag, key, value) {
  if (!bag || !key) return;
  const prop = new Cesium.ConstantProperty(value);
  try {
    if (bag[key] !== undefined) {
      bag[key] = prop;
    } else {
      bag.addProperty(key, prop);
    }
  } catch {
    try {
      bag[key] = prop;
    } catch {}
  }
}

function idsEqual(a, b) {
  const na = Number(a);
  const nb = Number(b);
  if (Number.isFinite(na) && Number.isFinite(nb)) return na === nb;
  if (a == null || b == null) return false;
  return String(a) === String(b);
}

function updateLaneletRefsForMovedLine(laneletDs, oldLineId, newLineId, newLineType) {
  if (!laneletDs?.entities?.values?.length) return 0;
  if (oldLineId == null || newLineId == null || !newLineType) return 0;

  let changed = 0;
  for (const lanelet of laneletDs.entities.values) {
    const props = getEntityPropsPlain(lanelet);
    const rightId = props?.[LANELET_REL.RIGHT_ID];
    const leftId = props?.[LANELET_REL.LEFT_ID];

    const hitRight = idsEqual(rightId, oldLineId);
    const hitLeft = idsEqual(leftId, oldLineId);
    if (!hitRight && !hitLeft) continue;

    const bag = ensurePropertyBag(lanelet);
    if (!bag) continue;

    if (hitRight) {
      setBagConst(bag, LANELET_REL.RIGHT_ID, newLineId);
      setBagConst(bag, LANELET_REL.RIGHT_TYPE, newLineType);
    }
    if (hitLeft) {
      setBagConst(bag, LANELET_REL.LEFT_ID, newLineId);
      setBagConst(bag, LANELET_REL.LEFT_TYPE, newLineType);
    }

    changed++;
  }

  return changed;
}

function findTargetLayerByKey(mapObjects, key) {
  const list = Array.isArray(mapObjects) ? mapObjects : [];
  for (const o of list) {
    if (!o?.file_name || !o?.dataSource) continue;
    if (matchKeyInText(key, o.file_name)) return o;
  }
  return null;
}

function findSourceContainerForEntity(viewer, mapObjects, entity) {
  const list = Array.isArray(mapObjects) ? mapObjects : [];

  for (const o of list) {
    const ds = o?.dataSource;
    if (!ds?.entities?.contains) continue;
    try {
      if (ds.entities.contains(entity)) {
        return { type: "datasource", container: ds, file_name: o.file_name };
      }
    } catch {}
  }

  try {
    if (viewer?.entities?.contains?.(entity)) {
      return { type: "viewer", container: viewer.entities, file_name: "viewer.entities" };
    }
  } catch {}

  return null;
}

function markLayerEditedByFileName(mapObjects, fileName) {
  if (!fileName) return { mapObjects, changed: false };

  const list = Array.isArray(mapObjects) ? mapObjects : [];
  let changed = false;

  const next = list.map((o) => {
    if (!o || o.file_name !== fileName) return o;

    changed = true;

    // keep ds reference, but also flag it
    if (o.dataSource) o.dataSource.is_edited = true;

    return {
      ...o,
      is_edited: true,
    };
  });

  return { mapObjects: next, changed };
}

function getMaxIdFromDataSource(ds) {
  if (!ds?.entities?.values) return null;
  const now = Cesium.JulianDate.now();
  let max = -Infinity;

  for (const e of ds.entities.values) {
    let v = {};
    try {
      v = e?.properties?.getValue?.(now) || {};
    } catch {}
    const n = Number(v?.id);
    if (Number.isFinite(n) && n > max) max = n;
  }

  return max === -Infinity ? null : max;
}

function getNextIdFromDataSource(ds) {
  const max = getMaxIdFromDataSource(ds);
  const base = Number.isFinite(max) ? max : 0;
  return base + 2;
}

function copyProps(src, dst, keys) {
  if (!src || !dst) return;
  for (const k of keys) {
    if (src[k] !== undefined) dst[k] = src[k];
  }
}

const STYLE_PROPS = {
  point: ["pixelSize", "color", "outlineColor", "outlineWidth", "scaleByDistance", "translucencyByDistance", "distanceDisplayCondition", "heightReference"],
  polyline: ["material", "width", "clampToGround", "classificationType", "zIndex", "distanceDisplayCondition", "shadows", "depthFailMaterial", "arcType", "disableDepthTestDistance"],
  polygon: ["material", "outline", "outlineColor", "outlineWidth", "classificationType", "zIndex", "shadows", "stRotation", "distanceDisplayCondition", "disableDepthTestDistance"],
  billboard: [
    "image",
    "color",
    "scale",
    "rotation",
    "alignedAxis",
    "heightReference",
    "verticalOrigin",
    "horizontalOrigin",
    "disableDepthTestDistance",
    "distanceDisplayCondition",
    "scaleByDistance",
    "translucencyByDistance",
  ],
  label: [
    "text",
    "font",
    "fillColor",
    "outlineColor",
    "outlineWidth",
    "style",
    "scale",
    "pixelOffset",
    "eyeOffset",
    "heightReference",
    "distanceDisplayCondition",
    "disableDepthTestDistance",
    "translucencyByDistance",
    "scaleByDistance",
  ],
};

function applyTemplateStyle(targetEntity, templateEntity) {
  if (!templateEntity || !targetEntity) return;

  if (templateEntity.point && targetEntity.point) copyProps(templateEntity.point, targetEntity.point, STYLE_PROPS.point);
  if (templateEntity.polyline && targetEntity.polyline) copyProps(templateEntity.polyline, targetEntity.polyline, STYLE_PROPS.polyline);
  if (templateEntity.polygon && targetEntity.polygon) copyProps(templateEntity.polygon, targetEntity.polygon, STYLE_PROPS.polygon);
  if (templateEntity.billboard && targetEntity.billboard) copyProps(templateEntity.billboard, targetEntity.billboard, STYLE_PROPS.billboard);
  if (templateEntity.label && targetEntity.label) copyProps(templateEntity.label, targetEntity.label, STYLE_PROPS.label);
}

function snapshotStyle(entity) {
  const s = {};

  if (entity.point) {
    s.point = {
      pixelSize: entity.point.pixelSize,
      color: entity.point.color,
      outlineColor: entity.point.outlineColor,
      outlineWidth: entity.point.outlineWidth,
    };
  }
  if (entity.polyline) {
    s.polyline = {
      material: entity.polyline.material,
      width: entity.polyline.width,
      disableDepthTestDistance: entity.polyline.disableDepthTestDistance,
    };
  }
  if (entity.polygon) {
    s.polygon = {
      material: entity.polygon.material,
      outline: entity.polygon.outline,
      outlineColor: entity.polygon.outlineColor,
      outlineWidth: entity.polygon.outlineWidth,
      disableDepthTestDistance: entity.polygon.disableDepthTestDistance,
    };
  }
  if (entity.billboard) {
    s.billboard = {
      color: entity.billboard.color,
      scale: entity.billboard.scale,
      image: entity.billboard.image,
    };
  }
  if (entity.label) {
    s.label = {
      fillColor: entity.label.fillColor,
      outlineColor: entity.label.outlineColor,
      outlineWidth: entity.label.outlineWidth,
      scale: entity.label.scale,
    };
  }

  return s;
}

function applySelectedStyle(entity) {
  const red = Cesium.Color.RED;

  if (entity.point) {
    const old = entity.point.pixelSize;
    const oldNum = typeof old === "number" ? old : 10;
    entity.point.color = red;
    entity.point.outlineColor = red;
    entity.point.outlineWidth = 2;
    entity.point.pixelSize = oldNum + 3;
    return;
  }

  if (entity.polyline) {
    const oldW = entity.polyline.width;
    const oldNum = typeof oldW === "number" ? oldW : 2;
    entity.polyline.material = new Cesium.ColorMaterialProperty(red);
    entity.polyline.width = oldNum + 2;
    entity.polyline.disableDepthTestDistance = Number.POSITIVE_INFINITY;
    return;
  }

  if (entity.polygon) {
    const ow = entity.polygon.outlineWidth;
    const oldNum = typeof ow === "number" ? ow : 1;
    entity.polygon.outline = true;
    entity.polygon.outlineColor = red;
    entity.polygon.outlineWidth = oldNum + 2;
    entity.polygon.material = Cesium.Color.RED.withAlpha(0.2);
    entity.polygon.disableDepthTestDistance = Number.POSITIVE_INFINITY;
    return;
  }

  if (entity.billboard) {
    const oldScale = entity.billboard.scale;
    const s = typeof oldScale === "number" ? oldScale : 1;
    entity.billboard.color = red;
    entity.billboard.scale = s * 1.15;
    return;
  }

  if (entity.label) {
    const oldScale = entity.label.scale;
    const s = typeof oldScale === "number" ? oldScale : 1;
    entity.label.fillColor = red;
    entity.label.outlineColor = red;
    entity.label.outlineWidth = 2;
    entity.label.scale = s * 1.1;
  }
}

function restoreStyle(entity, snap) {
  if (!entity || !snap) return;

  if (snap.point && entity.point) {
    entity.point.pixelSize = snap.point.pixelSize;
    entity.point.color = snap.point.color;
    entity.point.outlineColor = snap.point.outlineColor;
    entity.point.outlineWidth = snap.point.outlineWidth;
  }
  if (snap.polyline && entity.polyline) {
    entity.polyline.material = snap.polyline.material;
    entity.polyline.width = snap.polyline.width;
    entity.polyline.disableDepthTestDistance = snap.polyline.disableDepthTestDistance;
  }
  if (snap.polygon && entity.polygon) {
    entity.polygon.material = snap.polygon.material;
    entity.polygon.outline = snap.polygon.outline;
    entity.polygon.outlineColor = snap.polygon.outlineColor;
    entity.polygon.outlineWidth = snap.polygon.outlineWidth;
    entity.polygon.disableDepthTestDistance = snap.polygon.disableDepthTestDistance;
  }
  if (snap.billboard && entity.billboard) {
    entity.billboard.color = snap.billboard.color;
    entity.billboard.scale = snap.billboard.scale;
    entity.billboard.image = snap.billboard.image;
  }
  if (snap.label && entity.label) {
    entity.label.fillColor = snap.label.fillColor;
    entity.label.outlineColor = snap.label.outlineColor;
    entity.label.outlineWidth = snap.label.outlineWidth;
    entity.label.scale = snap.label.scale;
  }
}

function buildNewEntityOptionsFromEntity(entity) {
  const opts = {
    id: createUniqueId(),
    name: entity.name,
    description: entity.description,
    availability: entity.availability,
  };

  if (entity.position) opts.position = entity.position;
  if (entity.orientation) opts.orientation = entity.orientation;
  if (entity.viewFrom) opts.viewFrom = entity.viewFrom;

  if (entity.point) opts.point = entity.point;
  if (entity.polyline) opts.polyline = entity.polyline;
  if (entity.polygon) opts.polygon = entity.polygon;

  if (entity.billboard) opts.billboard = entity.billboard;
  if (entity.label) opts.label = entity.label;

  if (entity.corridor) opts.corridor = entity.corridor;
  if (entity.rectangle) opts.rectangle = entity.rectangle;
  if (entity.ellipse) opts.ellipse = entity.ellipse;
  if (entity.wall) opts.wall = entity.wall;
  if (entity.model) opts.model = entity.model;
  if (entity.path) opts.path = entity.path;
  if (entity.polylineVolume) opts.polylineVolume = entity.polylineVolume;
  if (entity.box) opts.box = entity.box;
  if (entity.cylinder) opts.cylinder = entity.cylinder;
  if (entity.plane) opts.plane = entity.plane;

  return opts;
}

function normalizePopupPropsOnly(userProps) {
  const out = {};
  if (!userProps || typeof userProps !== "object") return out;

  for (const k of Object.keys(userProps)) {
    const key = String(k);
    if (!key.trim()) continue;
    if (key.toLowerCase() === "id") continue; // id is auto
    out[key] = userProps[k];
  }

  return out;
}

// ----------------------
// Fast + accurate box hit test (no drillPick scanning)
// ----------------------

function getValueMaybe(prop, t) {
  if (!prop) return undefined;
  if (typeof prop.getValue === "function") return prop.getValue(t);
  return prop;
}

function collectPolygonPositions(h) {
  const out = [];
  if (!h) return out;
  if (Array.isArray(h.positions)) out.push(...h.positions);
  if (Array.isArray(h.holes)) {
    for (const hole of h.holes) out.push(...collectPolygonPositions(hole));
  }
  return out;
}

function rectCornersToCartesians(scene, rectRadians, heightMeters = 0) {
  const ellipsoid = scene?.globe?.ellipsoid || Cesium.Ellipsoid.WGS84;
  const r = rectRadians;
  if (!r) return [];

  const sw = new Cesium.Cartographic(r.west, r.south, heightMeters);
  const se = new Cesium.Cartographic(r.east, r.south, heightMeters);
  const ne = new Cesium.Cartographic(r.east, r.north, heightMeters);
  const nw = new Cesium.Cartographic(r.west, r.north, heightMeters);

  return [
    ellipsoid.cartographicToCartesian(sw),
    ellipsoid.cartographicToCartesian(se),
    ellipsoid.cartographicToCartesian(ne),
    ellipsoid.cartographicToCartesian(nw),
    ellipsoid.cartographicToCartesian(sw),
  ];
}

function ellipseSampleToCartesians(center, scene, semiMajor, semiMinor, heightMeters = 0, steps = 8) {
  if (!center || !Number.isFinite(semiMajor) || !Number.isFinite(semiMinor)) return [];
  const out = [];

  const ellipsoid = scene?.globe?.ellipsoid || Cesium.Ellipsoid.WGS84;

  let carto = null;
  try {
    carto = ellipsoid.cartesianToCartographic(center);
  } catch {}
  if (!carto) return [center];

  const c = ellipsoid.cartographicToCartesian(new Cesium.Cartographic(carto.longitude, carto.latitude, heightMeters));

  let enu = null;
  try {
    enu = Cesium.Transforms.eastNorthUpToFixedFrame(c);
  } catch {
    return [c];
  }

  for (let i = 0; i < steps; i++) {
    const a = (i / steps) * Math.PI * 2;
    const local = new Cesium.Cartesian3(Math.cos(a) * semiMajor, Math.sin(a) * semiMinor, 0);
    const world = new Cesium.Cartesian3();
    Cesium.Matrix4.multiplyByPoint(enu, local, world);
    out.push(world);
  }
  out.push(out[0]);
  return out;
}

function getEntityWorldPositions(scene, entity, t) {
  if (!entity) return [];

  if (entity.polyline?.positions) {
    const ps = getValueMaybe(entity.polyline.positions, t);
    return Array.isArray(ps) ? ps : [];
  }

  if (entity.corridor?.positions) {
    const ps = getValueMaybe(entity.corridor.positions, t);
    return Array.isArray(ps) ? ps : [];
  }

  if (entity.polygon?.hierarchy) {
    const h = getValueMaybe(entity.polygon.hierarchy, t);
    return collectPolygonPositions(h);
  }

  if (entity.wall?.positions) {
    const ps = getValueMaybe(entity.wall.positions, t);
    return Array.isArray(ps) ? ps : [];
  }

  if (entity.polylineVolume?.positions) {
    const ps = getValueMaybe(entity.polylineVolume.positions, t);
    return Array.isArray(ps) ? ps : [];
  }

  if (entity.rectangle?.coordinates) {
    const rr = getValueMaybe(entity.rectangle.coordinates, t);
    let h = 0;
    const hp = entity.rectangle.height;
    const hv = getValueMaybe(hp, t);
    if (Number.isFinite(hv)) h = hv;
    return rectCornersToCartesians(scene, rr, h);
  }

  if (entity.ellipse && entity.position) {
    const center = getValueMaybe(entity.position, t);
    const a = getValueMaybe(entity.ellipse.semiMajorAxis, t);
    const b = getValueMaybe(entity.ellipse.semiMinorAxis, t);
    let h = 0;
    const hp = entity.ellipse.height;
    const hv = getValueMaybe(hp, t);
    if (Number.isFinite(hv)) h = hv;
    const pts = ellipseSampleToCartesians(center, scene, a, b, h, 8);
    return Array.isArray(pts) ? pts : [];
  }

  if (entity.position) {
    const p = getValueMaybe(entity.position, t);
    return p ? [p] : [];
  }

  return [];
}

const _tmp2 = new Cesium.Cartesian2();

function toCanvas(scene, world) {
  if (!world) return null;

  if (typeof scene.cartesianToCanvasCoordinates === "function") {
    const r = scene.cartesianToCanvasCoordinates(world, _tmp2);
    return r ? { x: r.x, y: r.y } : null;
  }

  const r = Cesium.SceneTransforms.wgs84ToWindowCoordinates(scene, world, _tmp2);
  return r ? { x: r.x, y: r.y } : null;
}

function inflateRect(rect, pad) {
  return {
    left: rect.left - pad,
    right: rect.right + pad,
    top: rect.top - pad,
    bottom: rect.bottom + pad,
  };
}

function pointInRect(p, r) {
  return p.x >= r.left && p.x <= r.right && p.y >= r.top && p.y <= r.bottom;
}

// Cohen–Sutherland
function _outCode(x, y, r) {
  let c = 0;
  if (x < r.left) c |= 1;
  else if (x > r.right) c |= 2;
  if (y < r.top) c |= 4;
  else if (y > r.bottom) c |= 8;
  return c;
}

function segmentIntersectsRect(a, b, r) {
  let x0 = a.x,
    y0 = a.y,
    x1 = b.x,
    y1 = b.y;
  let c0 = _outCode(x0, y0, r);
  let c1 = _outCode(x1, y1, r);

  while (true) {
    if ((c0 | c1) === 0) return true;
    if ((c0 & c1) !== 0) return false;

    const cOut = c0 !== 0 ? c0 : c1;
    let x = 0,
      y = 0;

    if (cOut & 8) {
      x = x0 + ((x1 - x0) * (r.bottom - y0)) / (y1 - y0);
      y = r.bottom;
    } else if (cOut & 4) {
      x = x0 + ((x1 - x0) * (r.top - y0)) / (y1 - y0);
      y = r.top;
    } else if (cOut & 2) {
      y = y0 + ((y1 - y0) * (r.right - x0)) / (x1 - x0);
      x = r.right;
    } else {
      y = y0 + ((y1 - y0) * (r.left - x0)) / (x1 - x0);
      x = r.left;
    }

    if (cOut === c0) {
      x0 = x;
      y0 = y;
      c0 = _outCode(x0, y0, r);
    } else {
      x1 = x;
      y1 = y;
      c1 = _outCode(x1, y1, r);
    }
  }
}

function pointInPolygon(pt, vs) {
  let inside = false;
  for (let i = 0, j = vs.length - 1; i < vs.length; j = i++) {
    const xi = vs[i].x,
      yi = vs[i].y;
    const xj = vs[j].x,
      yj = vs[j].y;
    const intersect = yi > pt.y !== yj > pt.y && pt.x < ((xj - xi) * (pt.y - yi)) / (yj - yi + 1e-12) + xi;
    if (intersect) inside = !inside;
  }
  return inside;
}

function getNumericWidth(prop, t, fallback) {
  if (typeof prop === "number") return prop;
  if (prop && typeof prop.getValue === "function") {
    const v = prop.getValue(t);
    if (typeof v === "number") return v;
  }
  return fallback;
}

function entityIntersectsRect(viewer, entity, rect) {
  const scene = viewer.scene;
  const t = Cesium.JulianDate.now();

  let pad = 5;
  if (entity.polyline) pad = Math.max(5, Math.ceil(getNumericWidth(entity.polyline.width, t, 2) / 2) + 4);
  else if (entity.corridor) pad = Math.max(6, Math.ceil(getNumericWidth(entity.corridor.width, t, 8) / 2) + 4);
  else if (entity.polygon || entity.rectangle || entity.ellipse) pad = 7;

  const r = inflateRect(rect, pad);
  const world = getEntityWorldPositions(scene, entity, t);
  if (!world || world.length === 0) return false;

  // Point-like
  if (entity.point || entity.billboard || entity.label || entity.model || entity.box || entity.cylinder || entity.plane) {
    const p = toCanvas(scene, world[0]);
    return p ? pointInRect(p, r) : false;
  }

  // Project vertices
  const pts = [];
  for (let i = 0; i < world.length; i++) {
    const p = toCanvas(scene, world[i]);
    if (p) pts.push(p);
  }
  if (pts.length === 0) return false;

  // Any vertex inside
  for (let i = 0; i < pts.length; i++) {
    if (pointInRect(pts[i], r)) return true;
  }

  // Any segment intersects
  for (let i = 1; i < pts.length; i++) {
    if (segmentIntersectsRect(pts[i - 1], pts[i], r)) return true;
  }

  // Area covers rect center
  if ((entity.polygon || entity.rectangle || entity.ellipse) && pts.length >= 3) {
    const center = { x: (rect.left + rect.right) / 2, y: (rect.top + rect.bottom) / 2 };
    if (pointInPolygon(center, pts)) return true;
  }

  return false;
}

function nextFrame() {
  return new Promise((r) => requestAnimationFrame(() => r()));
}

async function pickEntitiesInRectFast(viewer, rect, candidates, shouldCancel) {
  const out = [];
  const list = Array.isArray(candidates) ? candidates : [];
  const chunk = 250;

  for (let i = 0; i < list.length; i++) {
    if (shouldCancel?.()) return [];
    const ent = list[i];
    if (!ent || !isEntity(ent)) continue;
    if (ent.show === false) continue;

    if (entityIntersectsRect(viewer, ent, rect)) out.push(ent);

    if (i > 0 && i % chunk === 0) await nextFrame();
  }

  return out;
}

// ----------------------
// Controller
// ----------------------

export function createMoveToolController({ viewer, mapObjectsGetter, mapObjectsSetter, onSelectionChange, originalCameraControls }) {
  let enabled = false;
  let handler = null;

  // selected map: entityId -> { entity, snap }
  const selected = new Map();

  // drag box
  let dragging = false;
  let dragStart = null;
  let dragEnd = null;
  let dragMoved = false;

  // overlay
  let overlay = null;

  // camera lock (external utils)
  let cameraLocked = false;

  // cancel token for async box pick
  let pickToken = 0;

  function setMapObjectsGetter(nextGetter) {
    mapObjectsGetter = nextGetter;
  }

  function notifySelection() {
    onSelectionChange?.(selected.size);
  }

  function ensureOverlay() {
    if (overlay) return overlay;

    const c = viewer?.container;
    if (!c) return null;

    overlay = document.createElement("div");
    overlay.style.position = "absolute";
    overlay.style.left = "0px";
    overlay.style.top = "0px";
    overlay.style.width = "0px";
    overlay.style.height = "0px";
    overlay.style.border = "1px dashed red";
    overlay.style.background = "rgba(255,0,0,0.08)";
    overlay.style.pointerEvents = "none";
    overlay.style.display = "none";
    overlay.style.zIndex = "9999";

    c.style.position = c.style.position || "relative";
    c.appendChild(overlay);

    return overlay;
  }

  function hideOverlay() {
    if (!overlay) return;
    overlay.style.display = "none";
    overlay.style.width = "0px";
    overlay.style.height = "0px";
  }

  function showOverlay(rect) {
    const o = ensureOverlay();
    if (!o) return;

    o.style.display = "block";
    o.style.left = `${rect.left}px`;
    o.style.top = `${rect.top}px`;
    o.style.width = `${rect.width}px`;
    o.style.height = `${rect.height}px`;
  }

  function lockCamera() {
    if (cameraLocked) return;
    const ok = disableCameraControls(viewer, originalCameraControls);
    if (ok) cameraLocked = true;
  }

  function unlockCamera() {
    if (!cameraLocked) return;
    restoreCameraControls(viewer, originalCameraControls);
    originalCameraControls.current = null;
    cameraLocked = false;
  }

  function selectEntity(entity) {
    if (!entity?.id) return;
    const id = String(entity.id);
    if (selected.has(id)) return;

    const snap = snapshotStyle(entity);
    selected.set(id, { entity, snap });
    applySelectedStyle(entity);
    notifySelection();
  }

  function unselectEntity(entity) {
    if (!entity?.id) return;
    const id = String(entity.id);

    const item = selected.get(id);
    if (!item) return;

    restoreStyle(item.entity, item.snap);
    selected.delete(id);
    notifySelection();
  }

  function toggleSelect(entity) {
    if (!entity?.id) return;
    const id = String(entity.id);

    if (selected.has(id)) unselectEntity(entity);
    else selectEntity(entity);
  }

  function clearSelection() {
    for (const [, item] of selected) restoreStyle(item.entity, item.snap);
    selected.clear();
    notifySelection();
  }

  function gatherCandidates() {
    const out = [];
    const seen = new Set();

    // From mapObjects datasources
    const mos = mapObjectsGetter?.() || [];
    for (const o of mos) {
      const ds = o?.dataSource;
      const vals = ds?.entities?.values;
      if (!Array.isArray(vals)) continue;
      for (const e of vals) {
        if (!e?.id) continue;
        const id = String(e.id);
        if (seen.has(id)) continue;
        seen.add(id);
        out.push(e);
      }
    }

    // From viewer.entities
    const ve = viewer?.entities?.values;
    if (Array.isArray(ve)) {
      for (const e of ve) {
        if (!e?.id) continue;
        const id = String(e.id);
        if (seen.has(id)) continue;
        seen.add(id);
        out.push(e);
      }
    }

    return out;
  }

  function enable() {
    if (enabled) return;
    enabled = true;

    pickToken++;

    if (handler) {
      try {
        handler.destroy();
      } catch {}
      handler = null;
    }

    handler = new Cesium.ScreenSpaceEventHandler(viewer.scene.canvas);

    handler.setInputAction((movement) => {
      if (!enabled) return;

      lockCamera();

      dragging = true;
      dragMoved = false;
      dragStart = movement.position;
      dragEnd = movement.position;
      hideOverlay();
    }, Cesium.ScreenSpaceEventType.LEFT_DOWN);

    handler.setInputAction((movement) => {
      if (!enabled) return;
      if (!dragging || !dragStart) return;

      dragEnd = movement.endPosition;

      const dx = Math.abs(dragEnd.x - dragStart.x);
      const dy = Math.abs(dragEnd.y - dragStart.y);
      if (dx > 5 || dy > 5) dragMoved = true;

      if (dragMoved) showOverlay(clampRect(dragStart, dragEnd));
    }, Cesium.ScreenSpaceEventType.MOUSE_MOVE);

    handler.setInputAction(async (movement) => {
      if (!enabled) return;

      const endPos = movement.position;
      const startPos = dragStart;

      dragging = false;

      let rect = null;

      try {
        if (!startPos || !endPos) return;
        rect = clampRect(startPos, endPos);
      } finally {
        dragStart = null;
        dragEnd = null;
        dragMoved = false;
        hideOverlay();
        unlockCamera();
      }

      if (!rect) return;

      if (rect.width > 6 && rect.height > 6) {
        // Box selection adds entities
        const myToken = ++pickToken;
        const candidates = gatherCandidates();

        const found = await pickEntitiesInRectFast(viewer, rect, candidates, () => !enabled || myToken !== pickToken);

        if (!enabled || myToken !== pickToken) return;

        for (const ent of found) selectEntity(ent);
      } else {
        // Click toggle
        const picked = viewer.scene.pick(endPos);
        const ent = getEntityFromPick(picked);
        if (ent) toggleSelect(ent);
      }
    }, Cesium.ScreenSpaceEventType.LEFT_UP);

    ensureOverlay();
    notifySelection();
  }

  function disable() {
    if (!enabled) return;
    enabled = false;

    pickToken++;

    clearSelection();

    if (handler) {
      try {
        handler.destroy();
      } catch {}
      handler = null;
    }

    hideOverlay();
    unlockCamera();
  }

  function destroy() {
    disable();

    if (overlay) {
      try {
        overlay.remove();
      } catch {}
      overlay = null;
    }
  }

  async function moveSelectedToTargetLayer({ targetKey, userProps }) {
    const key = TARGET_KEYS[targetKey];
    if (!key) return;

    const mapObjects = mapObjectsGetter?.() || [];
    const laneletLayer = findTargetLayerByKey(mapObjects, LANELET_KEY);
    const laneletDs = laneletLayer?.dataSource || null;
    const newLineTypeForLanelet = LINE_TYPE_BY_TARGET_KEY[targetKey] || "";

    const targetLayer = findTargetLayerByKey(mapObjects, key);
    const targetDs = targetLayer?.dataSource || null;
    if (!targetDs) return;

    const templateEntity = targetDs.entities?.values?.[0] || null;

    const editedSourceFileNames = new Set();
    let laneletTouched = false;
    let movedAny = false;

    // Allocate ids sequentially
    let nextId = getNextIdFromDataSource(targetDs);

    // IMPORTANT: properties must be ONLY popup key/values (+ auto id)
    const popupPropsOnly = normalizePopupPropsOnly(userProps);

    // Freeze selection list
    const moving = Array.from(selected.values()).map((x) => x.entity);

    for (const entity of moving) {
      if (!isEntity(entity)) continue;

      // Avoid moving into same layer
      const srcLayerName = getEntityLayerName(entity);
      if (srcLayerName && matchKeyInText(key, srcLayerName)) continue;

      const srcInfo = findSourceContainerForEntity(viewer, mapObjects, entity);
      if (!srcInfo) continue;
      if (srcInfo.file_name && matchKeyInText(key, srcInfo.file_name)) continue;

      const oldProps = getEntityPropsPlain(entity);
      const oldLineId = oldProps?.id;

      // Build final props: ONLY popup props + id
      const finalProps = {
        ...popupPropsOnly,
        id: nextId,
      };
      nextId += 2;

      // Build new entity in target
      const opts = buildNewEntityOptionsFromEntity(entity);

      // Rebuild properties as PropertyBag
      const bag = new Cesium.PropertyBag({});
      for (const k of Object.keys(finalProps)) {
        bag.addProperty(k, new Cesium.ConstantProperty(finalProps[k]));
      }
      opts.properties = bag;

      const newEntity = targetDs.entities.add(opts);

      // Apply target style template
      applyTemplateStyle(newEntity, templateEntity);

      // Fix lanelet relationships if this line is referenced by any lanelet
      if (laneletDs && newLineTypeForLanelet && oldLineId != null) {
        const changed = updateLaneletRefsForMovedLine(laneletDs, oldLineId, finalProps.id, newLineTypeForLanelet);
        if (changed > 0) laneletTouched = true;
      }

      // Remove from source + any duplicated containers
      if (srcInfo.type === "datasource") {
        try {
          srcInfo.container.entities.remove(entity);
        } catch {}
      } else if (srcInfo.type === "viewer") {
        try {
          srcInfo.container.remove(entity);
        } catch {}
      }

      // Best-effort cleanup in case the same entity exists in multiple collections
      try {
        viewer?.entities?.remove?.(entity);
      } catch {}

      const mosCleanup = Array.isArray(mapObjects) ? mapObjects : [];
      for (const o of mosCleanup) {
        const ds = o?.dataSource;
        if (!ds?.entities?.remove) continue;
        try {
          ds.entities.remove(entity);
        } catch {}
      }

      movedAny = true;
      if (srcInfo.file_name && srcInfo.file_name !== "viewer.entities") {
        editedSourceFileNames.add(srcInfo.file_name);
      }
    }

    if (movedAny) {
      // Always flag datasource objects too
      if (targetLayer) targetLayer.is_edited = true;
      if (targetLayer?.dataSource) targetLayer.dataSource.is_edited = true;

      if (laneletTouched && laneletLayer) laneletLayer.is_edited = true;
      if (laneletTouched && laneletLayer?.dataSource) laneletLayer.dataSource.is_edited = true;

      // If we have a setter, update state immutably so UI/save logic sees it
      if (typeof mapObjectsSetter === "function") {
        let nextList = Array.isArray(mapObjects) ? mapObjects : [];

        // target layer
        if (targetLayer?.file_name) {
          const r1 = markLayerEditedByFileName(nextList, targetLayer.file_name);
          nextList = r1.mapObjects;
        }

        // source layers
        for (const fn of editedSourceFileNames) {
          const r2 = markLayerEditedByFileName(nextList, fn);
          nextList = r2.mapObjects;
        }

        mapObjectsSetter(nextList);
      } else {
        // fallback: best-effort mutation
        if (targetLayer?.file_name) {
          const r1 = markLayerEditedByFileName(mapObjects, targetLayer.file_name);
          // ignore
        }
        for (const fn of editedSourceFileNames) {
          const r2 = markLayerEditedByFileName(mapObjects, fn);
          // ignore
        }
      }
    }

    clearSelection();
  }

  return {
    enable,
    disable,
    destroy,
    clearSelection,
    moveSelectedToTargetLayer,
    setMapObjectsGetter,
    getSelectedCount: () => selected.size,
  };
}
